import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, LoginCredentials, AuthTokens, User } from '../../types/auth';
import { login as loginApi, getProfile, updateProfile as updateProfileApi } from '../../api/auth';

// localStorageからトークンを読み込む
const storedTokens = localStorage.getItem('tokens');
const tokens = storedTokens ? JSON.parse(storedTokens) : null;

const initialState: AuthState = {
  user: null,
  tokens: tokens,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  successMessage: null,
  profileRequestSent: false,
};

// ログイン
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const tokens = await loginApi(credentials.email, credentials.password);
      
      // トークンをローカルストレージに保存
      localStorage.setItem('tokens', JSON.stringify(tokens));
      
      // プロフィール情報を取得
      const user = await getProfile();
      
      return { user, tokens };
    } catch (error: any) {
      
      // 403エラーの場合、具体的なメッセージを表示
      if (error.response?.status === 403) {
        return rejectWithValue(error.response?.data?.error || 'このアカウントは無効化されています');
      }
      
      return rejectWithValue(error.response?.data?.error || error.response?.data?.detail || 'ログインに失敗しました');
    }
  }
);

// プロファイル取得
export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const user = await getProfile();
      return user;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.detail || 'プロファイルの取得に失敗しました');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    localStorage.removeItem('tokens');
  }
);

// プロフィール更新
export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (userData: any, { rejectWithValue }) => {
    try {
      const user = await updateProfileApi(userData);
      return user;
    } catch (error: any) {
      
      // シンプルなエラーメッセージの抽出
      if (error.response?.data) {
        // パスワードエラーを特別処理
        if (error.response.data.password) {
          let pwdError = error.response.data.password;
          
          // 配列の場合は最初の要素を使用
          if (Array.isArray(pwdError)) {
            pwdError = pwdError[0];
          }
          
          // ErrorDetailオブジェクトの場合の処理
          if (typeof pwdError === 'object' && pwdError !== null) {
            pwdError = JSON.stringify(pwdError);
          }
          
          // 文字列として処理
          const pwdErrorStr = String(pwdError);
          
          // エラーメッセージから不要な部分を削除
          const cleanedError = pwdErrorStr
            .replace(/ErrorDetail\(string=|\[|\]|'|"|, code='[^']*'\)/g, '')
            .trim();
          
          // パスワードエラーメッセージを表示
          return rejectWithValue(`パスワードエラー: ${cleanedError}`);
        }
        
        // パスワード確認エラー
        if (error.response.data.password_confirm) {
          let confirmError = error.response.data.password_confirm;
          
          if (Array.isArray(confirmError)) {
            confirmError = confirmError[0];
          }
          
          if (typeof confirmError === 'object' && confirmError !== null) {
            confirmError = JSON.stringify(confirmError);
          }
          
          const confirmErrorStr = String(confirmError)
            .replace(/ErrorDetail\(string=|\[|\]|'|"|, code='[^']*'\)/g, '')
            .trim();
            
          return rejectWithValue(`確認用パスワードエラー: ${confirmErrorStr}`);
        }
        
        // それ以外のフィールドエラー
        if (typeof error.response.data === 'object') {
          const firstErrorKey = Object.keys(error.response.data)[0];
          if (firstErrorKey) {
            let firstError = error.response.data[firstErrorKey];
            
            if (Array.isArray(firstError)) {
              firstError = firstError[0];
            }
            
            if (typeof firstError === 'object' && firstError !== null) {
              firstError = JSON.stringify(firstError);
            }
            
            const firstErrorStr = String(firstError)
              .replace(/ErrorDetail\(string=|\[|\]|'|"|, code='[^']*'\)/g, '')
              .trim();
              
            return rejectWithValue(`${firstErrorKey}: ${firstErrorStr}`);
          }
        }
      }
      
      // どのケースにも当てはまらない場合
      return rejectWithValue('プロフィールの更新に失敗しました。入力内容を確認してください。');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state: AuthState) => {
      state.error = null;
    },
    clearSuccessMessage: (state: AuthState) => {
      state.successMessage = null;
    },
    setSuccessMessage: (state: AuthState, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ログイン
      .addCase(login.pending, (state: AuthState) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state: AuthState, action: PayloadAction<{ user: User; tokens: AuthTokens }>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.error = null;
        // プロフィール取得呼び出し済みフラグを立てて、重複リクエストを防止
        state.profileRequestSent = true;
      })
      .addCase(login.rejected, (state: AuthState, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // プロファイル取得
      .addCase(fetchProfile.pending, (state: AuthState) => {
        state.isLoading = true;
        state.error = null;
        state.profileRequestSent = true;
      })
      .addCase(fetchProfile.fulfilled, (state: AuthState, action: PayloadAction<User>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.profileRequestSent = true;
      })
      .addCase(fetchProfile.rejected, (state: AuthState, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.profileRequestSent = true;
      })
      // ログアウト
      .addCase(logout.fulfilled, (state: AuthState) => {
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
        state.error = null;
        state.profileRequestSent = false;
      })
      // プロフィール更新
      .addCase(updateProfile.pending, (state: AuthState) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state: AuthState, action: PayloadAction<User>) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
        state.successMessage = 'プロフィール情報が更新されました';
      })
      .addCase(updateProfile.rejected, (state: AuthState, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSuccessMessage, setSuccessMessage } = authSlice.actions;
export default authSlice.reducer; 